import React, { useState, useRef, useMemo } from 'react';
import { useProgress } from '../contexts/ProgressContext';
import './FlowArrangement.css';

const FlowArrangement = () => {
  const { progress, updateGameProgress } = useProgress();
  const gameId = 'flow-arrangement';
  
  // Load saved progress
  const savedGame = progress.games[gameId];
  // level represents completed flows, so currentFlowIndex should be the next flow to play
  const completedFlows = savedGame?.level || 0;
  const TOTAL_FLOWS = 4; // Total number of flows
  const savedFlowIndex = Math.min(completedFlows, TOTAL_FLOWS - 1);
  const flows = [
    {
      id: 'transaction-flow',
      title: 'Level 1: Transaction Flow',
      description: 'Arrange the steps of a Miden transaction from start to finish',
      tiles: [
        { 
          id: 'wallet', 
          text: 'Wallet prepares transaction locally', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
              <line x1="1" y1="10" x2="23" y2="10"/>
            </svg>
          ), 
          correctPosition: 0 
        },
        { 
          id: 'vm', 
          text: 'VM runs it and produces a proof', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24"/>
            </svg>
          ), 
          correctPosition: 1 
        },
        { 
          id: 'proof', 
          text: 'Proof goes to operator / chain', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          ), 
          correctPosition: 2 
        },
        { 
          id: 'verify', 
          text: 'Network verifies proof and updates state', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          ), 
          correctPosition: 3 
        },
        { 
          id: 'chain', 
          text: 'Polygon chain', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="5" cy="12" r="3"/>
              <circle cx="19" cy="12" r="3"/>
              <line x1="8" y1="12" x2="16" y2="12"/>
            </svg>
          ), 
          correctPosition: 4 
        }
      ],
      correctOrder: ['wallet', 'vm', 'proof', 'verify', 'chain']
    },
    {
      id: 'proof-creation',
      title: 'Level 2: Proof Creation Flow',
      description: 'Arrange how a zero-knowledge proof is created and verified',
      tiles: [
        { 
          id: 'compute', 
          text: 'Compute locally', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="3" width="20" height="14" rx="2"/>
              <line x1="8" y1="21" x2="16" y2="21"/>
            </svg>
          ), 
          correctPosition: 0 
        },
        { 
          id: 'witness', 
          text: 'Use witness data', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          ), 
          correctPosition: 1 
        },
        { 
          id: 'generate', 
          text: 'Generate proof', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          ), 
          correctPosition: 2 
        },
        { 
          id: 'send', 
          text: 'Send to chain', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          ), 
          correctPosition: 3 
        },
        { 
          id: 'verify', 
          text: 'Chain verifies', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          ), 
          correctPosition: 4 
        }
      ],
      correctOrder: ['compute', 'witness', 'generate', 'send', 'verify']
    },
    {
      id: 'advanced-flow',
      title: 'Level 3: Complete ZK Flow',
      description: 'Arrange the complete zero-knowledge proof workflow with all steps',
      tiles: [
        { 
          id: 'initiate', 
          text: 'Initiate transaction', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
              <line x1="1" y1="10" x2="23" y2="10"/>
            </svg>
          ), 
          correctPosition: 0 
        },
        { 
          id: 'prepare', 
          text: 'Prepare inputs', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
          ), 
          correctPosition: 1 
        },
        { 
          id: 'execute', 
          text: 'Execute on VM', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24"/>
            </svg>
          ), 
          correctPosition: 2 
        },
        { 
          id: 'create-proof', 
          text: 'Create ZK proof', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          ), 
          correctPosition: 3 
        },
        { 
          id: 'optimize', 
          text: 'Optimize proof', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z"/>
            </svg>
          ), 
          correctPosition: 4 
        },
        { 
          id: 'transmit', 
          text: 'Transmit proof', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          ), 
          correctPosition: 5 
        },
        { 
          id: 'validate', 
          text: 'Chain validates', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          ), 
          correctPosition: 6 
        },
        { 
          id: 'confirm', 
          text: 'Transaction confirmed', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="5" cy="12" r="3"/>
              <circle cx="19" cy="12" r="3"/>
              <line x1="8" y1="12" x2="16" y2="12"/>
            </svg>
          ), 
          correctPosition: 7 
        }
      ],
      correctOrder: ['initiate', 'prepare', 'execute', 'create-proof', 'optimize', 'transmit', 'validate', 'confirm']
    },
    {
      id: 'note-lifecycle',
      title: 'Level 4: Note Lifecycle Flow',
      description: 'Arrange how notes are created, consumed, and managed in Miden',
      tiles: [
        { 
          id: 'create-note', 
          text: 'Create note', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
              <path d="M8 8h8M8 12h8M8 16h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          ), 
          correctPosition: 0 
        },
        { 
          id: 'store-note', 
          text: 'Store in account', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
              <line x1="1" y1="10" x2="23" y2="10"/>
            </svg>
          ), 
          correctPosition: 1 
        },
        { 
          id: 'consume-note', 
          text: 'Consume note', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24"/>
            </svg>
          ), 
          correctPosition: 2 
        },
        { 
          id: 'update-state', 
          text: 'Update account state', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
          ), 
          correctPosition: 3 
        },
        { 
          id: 'prove-consumption', 
          text: 'Prove consumption', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          ), 
          correctPosition: 4 
        },
        { 
          id: 'finalize', 
          text: 'Finalize transaction', 
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          ), 
          correctPosition: 5 
        }
      ],
      correctOrder: ['create-note', 'store-note', 'consume-note', 'update-state', 'prove-consumption', 'finalize']
    }
  ];

  const [currentFlowIndex, setCurrentFlowIndex] = useState(savedFlowIndex);
  const currentFlow = flows[currentFlowIndex];
  const tiles = currentFlow.tiles;

  const [arrangedTiles, setArrangedTiles] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [tileFeedback, setTileFeedback] = useState({}); // Track feedback for each tile position
  const attemptsRef = useRef(0);

  const handleTileClick = (tile) => {
    if (completed) return; // Don't allow changes after completion
    
    let newArranged;
    if (arrangedTiles.find(t => t.id === tile.id)) {
      // Remove from arrangement
      newArranged = arrangedTiles.filter(t => t.id !== tile.id);
      setArrangedTiles(newArranged);
      setCompleted(false);
      setShowHint(false);
      // Clear feedback for all tiles
      setTileFeedback({});
    } else {
      // Add to arrangement
      newArranged = [...arrangedTiles, tile];
      setArrangedTiles(newArranged);
      checkCompletion(newArranged);
    }
  };

  const checkCompletion = (arrangement) => {
    if (arrangement.length !== tiles.length) return;
    
    const isCorrect = arrangement.every((tile, index) => {
      return tile.correctPosition === index;
    });
    
    // Show feedback for all tiles when arrangement is complete
    const feedback = {};
    arrangement.forEach((tile, index) => {
      feedback[index] = tile.correctPosition === index ? 'correct' : 'incorrect';
    });
    setTileFeedback(feedback);
    
    if (isCorrect) {
      setCompleted(true);
      setShowHint(false);
      // Track progress - flow completed
      // level represents completed flows, so pass currentFlowIndex + 1 (1-based completed count)
      const completedFlowCount = currentFlowIndex + 1;
      const isFinalFlow = completedFlowCount === flows.length;
      updateGameProgress(gameId, 100, completedFlowCount, isFinalFlow, 'Transaction Flow');
    } else {
      // Show hint after completing an incorrect arrangement
      attemptsRef.current += 1;
      if (attemptsRef.current >= 2) {
        setShowHint(true);
      }
    }
  };

  const resetGame = () => {
    setArrangedTiles([]);
    setCompleted(false);
    setShowHint(false);
    setTileFeedback({});
    attemptsRef.current = 0;
  };

  const nextFlow = () => {
    if (currentFlowIndex < flows.length - 1) {
      const nextFlowIndex = currentFlowIndex + 1;
      setCurrentFlowIndex(nextFlowIndex);
      setArrangedTiles([]);
      setCompleted(false);
      setShowHint(false);
      setTileFeedback({});
      attemptsRef.current = 0;
      // Track progress - 
      const completedFlowCount = currentFlowIndex + 1;
      updateGameProgress(gameId, 0, completedFlowCount, false);
    }
  };

  const getCorrectOrderText = () => {
    return currentFlow.correctOrder.map(id => {
      const tile = tiles.find(t => t.id === id);
      return tile ? tile.text : id;
    }).join(' → ');
  };

  // Shuffle available tiles to make it more challenging
  // Use useMemo to maintain shuffle order until tiles change
  const shuffledTiles = useMemo(() => {
    const shuffled = [...tiles];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, [tiles]); // Re-shuffle when tiles change 

  const availableTiles = shuffledTiles.filter(tile => !arrangedTiles.find(t => t.id === tile.id));

  return (
    <div className="flow-arrangement">
      <div className="game-header">
        <h2>{currentFlow.title}</h2>
        <p>{currentFlow.description}</p>
        <div className="flow-progress">
          Flow {currentFlowIndex + 1} of {flows.length}
        </div>
      </div>

      <div className="flow-container">
        <div className="flow-zone">
          {arrangedTiles.length === 0 ? (
            <div className="empty-flow">
              <p>Click tiles below to start building the flow</p>
            </div>
          ) : (
            <div className="flow-tiles">
              {arrangedTiles.map((tile, index) => {
                const feedback = tileFeedback[index];
                const isCorrect = tile.correctPosition === index;
                const showFeedback = feedback === 'correct' || feedback === 'incorrect';
                
                return (
                  <React.Fragment key={tile.id}>
                    <div
                      className={`flow-tile ${completed && isCorrect ? 'correct' : ''} ${feedback === 'correct' ? 'tile-correct' : ''} ${feedback === 'incorrect' ? 'tile-incorrect' : ''}`}
                      onClick={() => handleTileClick(tile)}
                    >
                      {showFeedback && (
                        <div className="tile-feedback-icon">
                          {feedback === 'correct' ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <line x1="18" y1="6" x2="6" y2="18"/>
                              <line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          )}
                        </div>
                      )}
                      <div className="tile-icon">{tile.icon}</div>
                      <div className="tile-text">{tile.text}</div>
                    </div>
                    {index < arrangedTiles.length - 1 && (
                      <div className="flow-arrow">→</div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        <div className="available-tiles">
          <h3>Available Tiles:</h3>
          <div className="tiles-grid">
            {availableTiles.map((tile) => (
              <div
                key={tile.id}
                className="available-tile"
                onClick={() => handleTileClick(tile)}
              >
                <div className="tile-icon">{tile.icon}</div>
                <div className="tile-text">{tile.text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showHint && !completed && (
        <div className="hint-message">
          <strong>Hint:</strong> The correct order is: {getCorrectOrderText()}
        </div>
      )}

      {completed && (
        <div className="completion-message">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline-block', marginRight: '8px', verticalAlign: 'middle'}}>
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          {currentFlowIndex < flows.length - 1 
            ? "Perfect! You've arranged the flow correctly!" 
            : "Perfect! You've completed all flows!"}
        </div>
      )}

      <div className="game-actions">
        <button onClick={resetGame} className="reset-button">
          Reset
        </button>
        {completed && currentFlowIndex < flows.length - 1 && (
          <button onClick={nextFlow} className="next-flow-button">
            Next Flow →
          </button>
        )}
        {completed && currentFlowIndex === flows.length - 1 && (
          <button onClick={() => {
            setCurrentFlowIndex(0);
            setArrangedTiles([]);
            setCompleted(false);
            setShowHint(false);
            setTileFeedback({});
            attemptsRef.current = 0;
            // Reset progress tracking
            updateGameProgress(gameId, 0, 0, false);
          }} className="restart-button">
            Play Again
          </button>
        )}
      </div>

      <div className="game-explanation">
        <h4>What you're learning:</h4>
        <p>
          In Miden, transactions flow from your wallet through the Miden VM (which runs on your device), 
          where a proof is created. This proof is then sent to the Polygon chain for verification, 
          all while keeping your actual transaction data private.
        </p>
      </div>
    </div>
  );
};

export default FlowArrangement;

